import React, { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import MyLoader from "../Spinner/MyLoader";
import SessionManager from "../Utils/SessionManager";
import { Redirect } from "react-router-dom";
import { Modal } from "react-bootstrap";
import "../css/Admin_css/admin-customize.css";
import COLORS from "../resources/colors/COLORS";
import myToast from "../Utils/MyToast";

const MdlSelectEventParticipatingStudent = ({
  actionClose,
  actionHandleSelected,
  event_id,
}) => {
  const [loggedIn] = useState(localStorage.getItem("token") !== null);
  const [loadingStudent, setLoadingStudent] = useState(false);
  const [loadingGroups, setLoadingGroups] = useState(false);
  const [show] = useState(true);
  const [allParticipants, setAllParticipants] = useState([]);
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [participantGroups, setParticipantGroups] = useState([]);
  const [selectedGroupId, setSelectedGroupId] = useState("all");

  // Get filtered participants based on selected group
  const filteredParticipants = useMemo(() => {
    if (selectedGroupId === "all") {
      return allParticipants;
    }
    return allParticipants.filter(p => p.group_id === selectedGroupId);
  }, [allParticipants, selectedGroupId]);

  // Check if all participants are selected
  const isAllSelected = useMemo(() => {
    return filteredParticipants.length > 0 && 
           selectedStudents.length === filteredParticipants.length &&
           filteredParticipants.every(p => 
             selectedStudents.some(s => s.paricipant_id === p.paricipant_id)
           );
  }, [filteredParticipants, selectedStudents]);

  const getParticipantGroups = useCallback(() => {
    if (!event_id) return;
    
    setLoadingGroups(true);
    const SCHOOL_CODE = SessionManager.getSchoolCode();
    const session = SessionManager.getCurrentSession();
    
    if (!session || !session[0]?.session_id) {
      setLoadingGroups(false);
      return;
    }

    const SESSION_ID = session[0].session_id;
    const URL = `${process.env.REACT_APP_SERVER_NAME}${process.env.REACT_APP_TARGET_DIR}getEventParticipantGroups.php`;

    axios
      .get(URL, {
        params: {
          event_id,
          session_id: SESSION_ID,
          school_code: SCHOOL_CODE,
        },
      })
      .then((res) => res.data)
      .then((response) => {
        setLoadingGroups(false);
        if (response.api_status === "OK" && Array.isArray(response.data)) {
          setParticipantGroups(response.data || []);
        } else {
          setParticipantGroups([]);
        }
      })
      .catch((error) => {
        setLoadingGroups(false);
        console.error("Error fetching participant groups:", error);
        setParticipantGroups([]);
      });
  }, [event_id]);

  const getStudentList = useCallback(() => {
    if (!event_id) return;
    
    setLoadingStudent(true);
    const SCHOOL_CODE = SessionManager.getSchoolCode();
    const session = SessionManager.getCurrentSession();
    
    if (!session || !session[0]?.session_id) {
      setLoadingStudent(false);
      return;
    }

    const SESSION_ID = session[0].session_id;
    const URL = `${process.env.REACT_APP_SERVER_NAME}${process.env.REACT_APP_TARGET_DIR}getAllEventParticipants.php`;

    axios
      .get(URL, {
        params: {
          event_id,
          session_id: SESSION_ID,
          school_code: SCHOOL_CODE,
        },
      })
      .then((res) => res.data)
      .then((response) => {
        setLoadingStudent(false);
        if (response.api_status === "OK") {
          setAllParticipants(response.data || []);
        } else {
          myToast.notify_warning(response.api_remark || "Failed to load participants");
          setAllParticipants([]);
        }
      })
      .catch((error) => {
        setLoadingStudent(false);
        console.error("Error fetching participants:", error);
        myToast.notify_error("Error loading participants");
        setAllParticipants([]);
      });
  }, [event_id]);

  const handleGroupChange = useCallback((groupId) => {
    setSelectedGroupId(groupId);
    // Clear selection when group changes
    setSelectedStudents([]);
    localStorage.setItem("SelectedMultipleStudent", JSON.stringify([]));
  }, []);

  const handleStudentSelection = useCallback((e, participant) => {
    const isChecked = e.target.checked;
    
    setSelectedStudents(prev => {
      let updated;
      if (isChecked) {
        // Add participant if not already selected
        if (!prev.some(s => s.paricipant_id === participant.paricipant_id)) {
          updated = [...prev, participant];
        } else {
          updated = prev;
        }
      } else {
        // Remove participant
        updated = prev.filter(s => s.paricipant_id !== participant.paricipant_id);
      }
      
      localStorage.setItem("SelectedMultipleStudent", JSON.stringify(updated));
      return updated;
    });
  }, []);

  const handleSelectAll = useCallback(() => {
    if (isAllSelected) {
      // Deselect all
      setSelectedStudents([]);
      localStorage.setItem("SelectedMultipleStudent", JSON.stringify([]));
    } else {
      // Select all filtered participants
      if (filteredParticipants.length > 0) {
        setSelectedStudents([...filteredParticipants]);
        localStorage.setItem("SelectedMultipleStudent", JSON.stringify(filteredParticipants));
      } else {
        myToast.notify_warning("No participants to select");
      }
    }
  }, [isAllSelected, filteredParticipants]);

  const handleDone = useCallback(() => {
    if (selectedStudents.length === 0) {
      myToast.notify_warning("Please select at least one participant");
      return;
    }
    actionHandleSelected(selectedStudents);
  }, [selectedStudents, actionHandleSelected]);

  // Get participant count for each group
  const getGroupCount = useCallback((groupId) => {
    if (groupId === "all") {
      return allParticipants.length;
    }
    return allParticipants.filter(p => p.group_id === groupId).length;
  }, [allParticipants]);

  useEffect(() => {
    localStorage.setItem("SelectedMultipleStudent", JSON.stringify([]));
    if (event_id) {
      getParticipantGroups();
      getStudentList();
    }
  }, [event_id, getParticipantGroups, getStudentList]);

  if (!loggedIn) {
    return <Redirect to="/Login" />;
  }

  return (
    <Modal
      show={show}
      onHide={actionClose}
      animation={false}
      size="xl"
      centered
      dialogClassName="rounded-modal"
      contentClassName="rounded-modal-content"
    >
      <style>{`
        .rounded-modal .modal-content {
          border-radius: 20px !important;
          overflow: hidden !important;
          border: none !important;
        }
        .rounded-modal .modal-header {
          border-radius: 20px 20px 0 0 !important;
          border: none !important;
        }
        .rounded-modal .modal-body {
          border-radius: 0 0 20px 20px !important;
        }
        @keyframes pulse {
          0%, 100% {
            transform: scale(1);
            opacity: 1;
          }
          50% {
            transform: scale(1.1);
            opacity: 0.9;
          }
        }
      `}</style>
      <Modal.Header
        closeButton
        style={{
          background: `linear-gradient(135deg, ${COLORS.primary_color_shade6 || "#3b82f6"} 0%, ${COLORS.primary_color_shade8 || "#1e40af"} 100%)`,
          color: COLORS.white,
          border: "none",
          padding: "12px 16px",
        }}
      >
        <Modal.Title style={{ fontSize: "18px", fontWeight: 600 }}>
          <i className="fa fa-users" style={{ marginRight: "8px" }} />
          Select Participants
        </Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ padding: "16px", background: "#f8fafc" }}>
        {/* Group Selection Tabs */}
        {participantGroups.length > 0 && (
          <div style={{ 
            marginBottom: "12px", 
            display: "flex", 
            gap: "8px", 
            flexWrap: "wrap", 
            alignItems: "center",
            padding: "10px 12px",
            background: COLORS.white,
            borderRadius: "12px",
            boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)",
          }}>
            <button
              type="button"
              onClick={() => handleGroupChange("all")}
              style={{
                padding: "6px 16px",
                borderRadius: "8px",
                border: "none",
                background: selectedGroupId === "all" 
                  ? `linear-gradient(135deg, ${COLORS.primary_color_shade6 || "#3b82f6"} 0%, ${COLORS.primary_color_shade8 || "#1e40af"} 100%)`
                  : COLORS.white,
                color: selectedGroupId === "all" ? "#fff" : COLORS.black8,
                fontWeight: 600,
                fontSize: "13px",
                cursor: "pointer",
                transition: "all 0.3s ease",
                border: selectedGroupId === "all" ? "none" : `2px solid ${COLORS.grey3 || "#E0E0E0"}`,
                boxShadow: selectedGroupId === "all" ? "0 4px 12px rgba(59, 130, 246, 0.4)" : "0 2px 4px rgba(0, 0, 0, 0.05)",
              }}
              onMouseEnter={(e) => {
                if (selectedGroupId !== "all") {
                  e.currentTarget.style.borderColor = COLORS.primary_color_shade6;
                  e.currentTarget.style.background = COLORS.primary_color_shade1;
                  e.currentTarget.style.transform = "translateY(-2px)";
                  e.currentTarget.style.boxShadow = "0 4px 8px rgba(59, 130, 246, 0.2)";
                }
              }}
              onMouseLeave={(e) => {
                if (selectedGroupId !== "all") {
                  e.currentTarget.style.borderColor = COLORS.grey3;
                  e.currentTarget.style.background = COLORS.white;
                  e.currentTarget.style.transform = "translateY(0)";
                  e.currentTarget.style.boxShadow = "0 2px 4px rgba(0, 0, 0, 0.05)";
                }
              }}
            >
              <i className="fa fa-list" style={{ marginRight: "6px" }} />
              All ({getGroupCount("all")})
            </button>
            {participantGroups.map((group) => {
              const groupCount = getGroupCount(group.group_id);
              return (
                <button
                  key={group.group_id}
                  type="button"
                  onClick={() => handleGroupChange(group.group_id)}
                  style={{
                    padding: "6px 16px",
                    borderRadius: "8px",
                    border: "none",
                    background: selectedGroupId === group.group_id 
                      ? `linear-gradient(135deg, ${COLORS.primary_color_shade6 || "#3b82f6"} 0%, ${COLORS.primary_color_shade8 || "#1e40af"} 100%)`
                      : COLORS.white,
                    color: selectedGroupId === group.group_id ? "#fff" : COLORS.black8,
                    fontWeight: 600,
                    fontSize: "13px",
                    cursor: "pointer",
                    transition: "all 0.3s ease",
                    border: selectedGroupId === group.group_id ? "none" : `2px solid ${COLORS.grey3 || "#E0E0E0"}`,
                    boxShadow: selectedGroupId === group.group_id ? "0 4px 12px rgba(59, 130, 246, 0.4)" : "0 2px 4px rgba(0, 0, 0, 0.05)",
                  }}
                  onMouseEnter={(e) => {
                    if (selectedGroupId !== group.group_id) {
                      e.currentTarget.style.borderColor = COLORS.primary_color_shade6;
                      e.currentTarget.style.background = COLORS.primary_color_shade1;
                      e.currentTarget.style.transform = "translateY(-2px)";
                      e.currentTarget.style.boxShadow = "0 4px 8px rgba(59, 130, 246, 0.2)";
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (selectedGroupId !== group.group_id) {
                      e.currentTarget.style.borderColor = COLORS.grey3;
                      e.currentTarget.style.background = COLORS.white;
                      e.currentTarget.style.transform = "translateY(0)";
                      e.currentTarget.style.boxShadow = "0 2px 4px rgba(0, 0, 0, 0.05)";
                    }
                  }}
                >
                  {group.group_name} ({groupCount})
                </button>
              );
            })}
          </div>
        )}

        {/* Selection Info and Select All Button */}
        <div style={{ 
          marginBottom: "12px", 
          padding: "10px 14px",
          background: COLORS.white,
          borderRadius: "12px",
          boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <div style={{
              width: "40px",
              height: "40px",
              borderRadius: "10px",
              background: `linear-gradient(135deg, ${COLORS.primary_color_shade6 || "#3b82f6"} 0%, ${COLORS.primary_color_shade8 || "#1e40af"} 100%)`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#fff",
              fontSize: "16px",
              fontWeight: "bold",
            }}>
              {selectedStudents.length}
            </div>
            <div>
              <div style={{ fontSize: "12px", color: "#6b7280", fontWeight: 500 }}>
                Selected
              </div>
              <div style={{ fontSize: "14px", color: COLORS.black8, fontWeight: 600 }}>
                {selectedStudents.length} / {filteredParticipants.length}
              </div>
            </div>
          </div>
          <button
            type="button"
            onClick={handleSelectAll}
            style={{
              padding: "8px 16px",
              borderRadius: "8px",
              border: "none",
              background: `linear-gradient(135deg, ${COLORS.primary_color_shade6 || "#3b82f6"} 0%, ${COLORS.primary_color_shade8 || "#1e40af"} 100%)`,
              color: "#fff",
              fontWeight: 600,
              fontSize: "13px",
              cursor: "pointer",
              transition: "all 0.3s ease",
              boxShadow: "0 2px 8px rgba(59, 130, 246, 0.3)",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = "translateY(-2px)";
              e.currentTarget.style.boxShadow = "0 4px 12px rgba(59, 130, 246, 0.4)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "translateY(0)";
              e.currentTarget.style.boxShadow = "0 2px 8px rgba(59, 130, 246, 0.3)";
            }}
          >
            <i className={`fa ${isAllSelected ? "fa-check-square" : "fa-square"}`} style={{ marginRight: "6px" }} />
            {isAllSelected ? "Deselect All" : "Select All"}
          </button>
        </div>

        {/* Participant List */}
        <div style={{ 
          maxHeight: "450px", 
          overflowY: "auto",
          paddingRight: "6px",
        }}>
          {loadingStudent ? (
            <div style={{ padding: "40px", textAlign: "center" }}>
              <MyLoader />
            </div>
          ) : filteredParticipants.length > 0 ? (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: "14px" }}>
              {filteredParticipants.map((item, index) => {
                const isSelected = selectedStudents.some(s => s.paricipant_id === item.paricipant_id);
                return (
                  <div
                    key={item.paricipant_id || index}
                    style={{
                      background: isSelected 
                        ? `linear-gradient(135deg, ${COLORS.primary_color_shade1 || "#eff6ff"} 0%, ${COLORS.white} 100%)`
                        : COLORS.white,
                      borderRadius: "14px",
                      padding: "14px",
                      boxShadow: isSelected 
                        ? `0 6px 20px rgba(59, 130, 246, 0.25)` 
                        : "0 2px 10px rgba(0, 0, 0, 0.06)",
                      border: isSelected 
                        ? `3px solid ${COLORS.primary_color_shade6 || "#3b82f6"}` 
                        : "2px solid #e5e7eb",
                      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                      cursor: "pointer",
                      position: "relative",
                      overflow: "visible",
                      boxSizing: "border-box",
                    }}
                    onClick={(e) => {
                      if (e.target.type !== "checkbox") {
                        const checkbox = e.currentTarget.querySelector('input[type="checkbox"]');
                        if (checkbox) {
                          checkbox.click();
                        }
                      }
                    }}
                    onMouseEnter={(e) => {
                      if (!isSelected) {
                        e.currentTarget.style.transform = "translateY(-4px) scale(1.01)";
                        e.currentTarget.style.boxShadow = "0 8px 24px rgba(0, 0, 0, 0.12)";
                        e.currentTarget.style.borderColor = COLORS.primary_color_shade6;
                        e.currentTarget.style.borderWidth = "2px";
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!isSelected) {
                        e.currentTarget.style.transform = "translateY(0) scale(1)";
                        e.currentTarget.style.boxShadow = "0 2px 10px rgba(0, 0, 0, 0.06)";
                        e.currentTarget.style.borderColor = "#e5e7eb";
                        e.currentTarget.style.borderWidth = "2px";
                      }
                    }}
                  >
                    {/* Selection Indicator Bar */}
                    {isSelected && (
                      <div style={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        right: 0,
                        height: "4px",
                        background: `linear-gradient(90deg, ${COLORS.primary_color_shade6 || "#3b82f6"} 0%, ${COLORS.primary_color_shade8 || "#1e40af"} 100%)`,
                      }} />
                    )}

                    {/* Checkbox Badge */}
                    {isSelected && (
                      <div style={{
                        position: "absolute",
                        top: "12px",
                        right: "12px",
                        width: "32px",
                        height: "32px",
                        borderRadius: "50%",
                        background: `linear-gradient(135deg, ${COLORS.primary_color_shade6 || "#3b82f6"} 0%, ${COLORS.primary_color_shade8 || "#1e40af"} 100%)`,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "#fff",
                        fontSize: "14px",
                        boxShadow: "0 4px 12px rgba(59, 130, 246, 0.5)",
                        animation: "pulse 2s infinite",
                      }}>
                        <i className="fa fa-check" />
                      </div>
                    )}
                    
                    <div style={{ display: "flex", gap: "14px", alignItems: "flex-start" }}>
                      {/* Avatar */}
                      <div style={{ flexShrink: 0, position: "relative" }}>
                        <div style={{
                          width: "68px",
                          height: "68px",
                          borderRadius: "14px",
                          overflow: "hidden",
                          background: `linear-gradient(135deg, ${COLORS.primary_color_shade6 || "#3b82f6"} 0%, ${COLORS.primary_color_shade8 || "#1e40af"} 100%)`,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          boxShadow: isSelected 
                            ? "0 6px 16px rgba(59, 130, 246, 0.4)" 
                            : "0 4px 12px rgba(59, 130, 246, 0.25)",
                          border: isSelected ? "3px solid rgba(255, 255, 255, 0.8)" : "2px solid rgba(255, 255, 255, 0.5)",
                          transition: "all 0.3s ease",
                        }}>
                          {item.image_dir ? (
                            <img 
                              src={item.image_dir} 
                              alt={item.student_name}
                              style={{ width: "100%", height: "100%", objectFit: "cover" }}
                            />
                          ) : (
                            <span style={{ color: "#fff", fontSize: "28px", fontWeight: "bold", textShadow: "0 2px 4px rgba(0,0,0,0.2)" }}>
                              {item.student_name ? item.student_name.charAt(0).toUpperCase() : "?"}
                            </span>
                          )}
                        </div>
                        {/* Participant Type Badge */}
                        {item.participant_type && (
                          <div style={{
                            position: "absolute",
                            bottom: "-4px",
                            right: "-4px",
                            background: COLORS.primary_color_shade6,
                            color: "#fff",
                            fontSize: "9px",
                            fontWeight: 600,
                            padding: "2px 6px",
                            borderRadius: "8px",
                            boxShadow: "0 2px 6px rgba(59, 130, 246, 0.4)",
                            whiteSpace: "nowrap",
                          }}>
                            {item.participant_type}
                          </div>
                        )}
                      </div>

                      {/* Details */}
                      <div style={{ flex: 1, minWidth: 0, paddingTop: "2px" }}>
                        <h6 style={{ 
                          margin: "0 0 8px 0", 
                          fontSize: "16px", 
                          fontWeight: 700,
                          color: isSelected ? COLORS.primary_color_shade8 : COLORS.black8,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          letterSpacing: "-0.2px",
                        }}>
                          {item.student_name}
                        </h6>
                        <div style={{ display: "flex", flexDirection: "column", gap: "5px" }}>
                          <div style={{ 
                            fontSize: "12px", 
                            color: "#64748b",
                            display: "flex",
                            alignItems: "center",
                            gap: "6px",
                          }}>
                            <div style={{
                              width: "16px",
                              height: "16px",
                              borderRadius: "4px",
                              background: "#f1f5f9",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              color: COLORS.primary_color_shade6,
                              fontSize: "9px",
                            }}>
                              <i className="fa fa-user" />
                            </div>
                            <span style={{ fontWeight: 500 }}>{item.father_name || "N/A"}</span>
                          </div>
                          <div style={{ 
                            fontSize: "12px", 
                            color: "#64748b",
                            display: "flex",
                            alignItems: "center",
                            gap: "6px",
                          }}>
                            <div style={{
                              width: "16px",
                              height: "16px",
                              borderRadius: "4px",
                              background: "#f1f5f9",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              color: COLORS.primary_color_shade6,
                              fontSize: "9px",
                            }}>
                              <i className="fa fa-phone" />
                            </div>
                            <span style={{ fontWeight: 500 }}>{item.contact_number || "N/A"}</span>
                          </div>
                          <div style={{ 
                            fontSize: "12px", 
                            color: "#64748b",
                            display: "flex",
                            alignItems: "center",
                            gap: "6px",
                          }}>
                            <div style={{
                              width: "16px",
                              height: "16px",
                              borderRadius: "4px",
                              background: "#f1f5f9",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              color: COLORS.primary_color_shade6,
                              fontSize: "9px",
                            }}>
                              <i className="fa fa-graduation-cap" />
                            </div>
                            <span style={{ fontWeight: 500 }}>{item.reading_class || "N/A"}</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Hidden Checkbox */}
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={(e) => handleStudentSelection(e, item)}
                      style={{ position: "absolute", opacity: 0, pointerEvents: "none" }}
                    />
                  </div>
                );
              })}
            </div>
          ) : (
            <div style={{ 
              padding: "40px 30px", 
              textAlign: "center", 
              background: COLORS.white,
              borderRadius: "12px",
              boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)",
            }}>
              <div style={{
                width: "60px",
                height: "60px",
                borderRadius: "50%",
                background: "#f1f5f9",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                margin: "0 auto 16px",
              }}>
                <i className="fa fa-users" style={{ fontSize: "28px", color: "#94a3b8" }} />
              </div>
              <p style={{ fontSize: "14px", color: "#64748b", fontWeight: 500, margin: 0 }}>
                {selectedGroupId === "all" 
                  ? "No participants found for this event."
                  : "No participants found in the selected group."}
              </p>
            </div>
          )}
        </div>

        {/* Done Button */}
        <div
          style={{ 
            marginTop: "12px",
            cursor: "pointer",
          }}
          onClick={handleDone}
        >
          <div
            style={{
              background: `linear-gradient(135deg, ${COLORS.primary_color_shade6 || "#3b82f6"} 0%, ${COLORS.primary_color_shade8 || "#1e40af"} 100%)`,
              color: "#fff",
              padding: "12px 20px",
              borderRadius: "12px",
              textAlign: "center",
              fontWeight: 600,
              fontSize: "15px",
              transition: "all 0.3s ease",
              boxShadow: "0 4px 12px rgba(59, 130, 246, 0.3)",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = "translateY(-2px)";
              e.currentTarget.style.boxShadow = "0 6px 16px rgba(59, 130, 246, 0.4)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "translateY(0)";
              e.currentTarget.style.boxShadow = "0 4px 12px rgba(59, 130, 246, 0.3)";
            }}
          >
            <i className="fa fa-check-circle" style={{ marginRight: "8px" }} />
            Done ({selectedStudents.length} {selectedStudents.length === 1 ? "Participant" : "Participants"})
          </div>
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default MdlSelectEventParticipatingStudent;
